import { isNil } from "lodash";

import {
  MAX_UNIT_OF_MEASURE_QUANTITY,
  MULTI_SHIPMENT_PROJECT_MAX_LOADS_AVAILABLE,
  MULTI_SHIPMENT_PROJECT_MIN_LOADS_AVAILABLE,
  NON_PUBLICLY_SHAREABLE_SHIPMENT_TYPES,
  SHIPMENT_SERVICE_VALUE_TO_LABEL_MAP,
} from "../constants";

export const AVAILABLE_LOADS_ERROR_MESSAGE = `Available loads must be a number between ${MULTI_SHIPMENT_PROJECT_MIN_LOADS_AVAILABLE} and ${MULTI_SHIPMENT_PROJECT_MAX_LOADS_AVAILABLE}.`;

// Worded as the API words its own rejection, so the shipper reads the same
// sentence whichever side catches the quantity.
export const DELIVERED_QUANTITY_TOO_LARGE_MESSAGE = `Delivered quantity must be ${MAX_UNIT_OF_MEASURE_QUANTITY} or less.`;

// Whether a delivered quantity is one the API can store.
export function deliveredQuantityIsStorable(
  quantity: number | null | undefined,
): boolean {
  return isNil(quantity) || quantity <= MAX_UNIT_OF_MEASURE_QUANTITY;
}

// Whether a loads count is one a multi shipment project can be created with.
// The API rejects anything outside of this range.
export function availableLoadsAreValid(
  availableLoads: number | null | undefined,
): availableLoads is number {
  return (
    !isNil(availableLoads) &&
    availableLoads >= MULTI_SHIPMENT_PROJECT_MIN_LOADS_AVAILABLE &&
    availableLoads <= MULTI_SHIPMENT_PROJECT_MAX_LOADS_AVAILABLE
  );
}

/*
Seeds the create shipment form's available loads from an existing project (a copy)
or from stored template content. A count the API would reject - including the 0
that templates saved with an empty loads field persisted - becomes an empty field,
so the shipper is asked for a real count instead of posting an invalid one.
*/
export function getSeededAvailableLoads(
  loadsAvailable: number | null | undefined,
): number | null {
  return availableLoadsAreValid(loadsAvailable) ? loadsAvailable : null;
}

// Whether a shipment of this type has a reachable public tracking page. Types in
// NON_PUBLICLY_SHAREABLE_SHIPMENT_TYPES 404 on the public endpoint, so no public
// link should be offered for them.
export function shipmentTypeSupportsPublicSharing(type: ShipmentType): boolean {
  return !NON_PUBLICLY_SHAREABLE_SHIPMENT_TYPES.includes(type);
}

// Whether a shipment with this notification setting is posted publicly
// (reaching carriers beyond the shipper's selected private network).
export function isPublicShipmentNotificationType(
  notificationType: ShipmentNotificationType,
): boolean {
  return (
    notificationType === "ALL_CARRIER_NETWORK" ||
    notificationType === "SELECTED_NETWORK_CARRIERS"
  );
}

export function buildPublicShipmentUrl(publicToken: string): string {
  return `${window.location.origin}/public/shipments/${publicToken}`;
}

// Whether a shipper's access to a shipment grants write privileges. Owner,
// company/branch admin, and read-and-write sharees all qualify; a future
// read-only access variant is excluded by omission. Mirror of
// carrierHasBookingWriteAccess for the shipper side.
export function shipperHasWriteAccess(accessType: ShipmentAccessType): boolean {
  return (
    accessType === "OWNER" ||
    accessType === "ADMIN" ||
    accessType === "SHAREE_READ_AND_WRITE"
  );
}

interface PublicTrackingUrlParams {
  publicToken: string | null;
  shipmentType: ShipmentType;
}

// The public tracking URL, but only when the shipment has a reachable public page:
// a live (non-revoked / accessible) token AND a type the public endpoint supports.
// Returns null otherwise so callers can fall back to the generic Evotrux URL.
export function getPublicShipmentTrackingUrlOrNull(
  params: PublicTrackingUrlParams,
): string | null {
  const { publicToken, shipmentType } = params;
  if (!publicToken || !shipmentTypeSupportsPublicSharing(shipmentType)) {
    return null;
  }
  return buildPublicShipmentUrl(publicToken);
}

interface CommodityLikeShipment {
  commodity?: Commodity | null | undefined;
  commodity_freetext: string | null;
}

export const getServiceTypeDisplayLabel = (serviceType: string): string =>
  SHIPMENT_SERVICE_VALUE_TO_LABEL_MAP[serviceType] || serviceType;

export function getCommdityNameForCondensedShipmentShipment(
  shipment: CommodityLikeShipment,
): string {
  if (shipment?.commodity?.name) {
    return shipment.commodity.name;
  }
  if (shipment?.commodity_freetext && shipment?.commodity_freetext !== "") {
    return shipment.commodity_freetext;
  }
  return "";
}

export function shipmentIsBooked(shipment: CondensedShipment) {
  return shipment.info.booked_at != null;
}

export function shipmentIsActive(shipment: CondensedShipment) {
  return (
    shipment.info.booked_at == null &&
    shipment.info.cancelled_at == null &&
    shipment.info.expired === false
  );
}

export function isBookNowEligibleShipmentType(type: ShipmentType): boolean {
  return type === "SINGLE" || type === "MULTI_SHIPMENT_PROJECT";
}

export function getBookNowPriceDisplay(
  shipment?: ShipperCondensedShipment,
): string {
  if (!shipment?.book_now_info?.is_book_now) return "";
  if (!isBookNowEligibleShipmentType(shipment.info.type)) return "";
  return shipment.book_now_info.book_now_price?.toString() ?? "";
}

export function getActiveFiltersCount(filters: ShipmentFilters | null): number {
  if (!filters) return 0;

  let filterCount = 0;

  if (filters.equipment_types && filters.equipment_types.length > 0)
    filterCount += 1;
  if (filters.services && filters.services.length > 0) filterCount += 1;
  if (filters.origin?.provinces && filters.origin.provinces.length > 0)
    filterCount += 1;
  if (
    filters.destination?.provinces &&
    filters.destination.provinces.length > 0
  ) {
    filterCount += 1;
  }
  if (filters.shipmentName && filters.shipmentName.trim() !== "")
    filterCount += 1;
  if (filters.status && filters.status.length > 0) filterCount += 1;
  if (filters.shipmentTypes && filters.shipmentTypes.length > 0)
    filterCount += 1;
  if (
    (filters.branches && filters.branches.length > 0) ||
    (filters.shippers && filters.shippers.length > 0) ||
    filters.includeCompanyShipments === true ||
    filters.includeBranchShipments === true
  )
    filterCount += 1;

  return filterCount;
}
